/**
 * DeductionsTab — manual salary deductions for the Payroll module.
 *
 * Distinct from GOSI/late/unpaid-leave which the payroll engine already
 * computes automatically. Here Payroll Admins can add one-off deductions
 * (disciplinary fines, damage compensation) or split instalment plans
 * (salary advances repaid over N cycles).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, XCircle, Warning, CaretDown, PencilSimple } from '@phosphor-icons/react';
import { EmployeeName } from '@/components/EmployeeName';
import useEmployeeBadges from '@/hooks/useEmployeeBadges';
import useEmployeesMap from '@/hooks/useEmployeesMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_BADGE = {
  active:    { ar: 'نشط',    en: 'Active',    cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  completed: { ar: 'مكتمل',  en: 'Completed', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  cancelled: { ar: 'ملغى',   en: 'Cancelled', cls: 'bg-gray-100 text-gray-700 border-gray-200' },
};

const fmtSar = (n) => Math.round(Number(n) || 0).toLocaleString();

export default function DeductionsTab({ employees = [] }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');

  const [openCreate, setOpenCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    user_id: '',
    reason: '',
    total_amount: '',
    monthly_instalment: '',
    start_year: new Date().getFullYear(),
    start_month: new Date().getMonth() + 1,
    notes: '',
  });

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  // Edit target — populated when admin clicks "Edit" on an eligible row
  // (active + not yet applied to any payroll cycle). Uses the SAME form
  // shape as create so we can re-use the input controls.
  const [editTarget, setEditTarget] = useState(null);
  const [editing, setEditing] = useState(false);

  const { getBadge } = useEmployeeBadges(employees);
  const { empNo } = useEmployeesMap();

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const r = await axios.get(`${API}/deductions`, { withCredentials: true, params });
      setRows(r?.data || []);
    } catch (_e) {
      toast.error(isAr ? 'فشل جلب الخصومات' : 'Failed to load deductions');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusFilter]);

  const resetForm = () => {
    setForm({
      user_id: '', reason: '', total_amount: '', monthly_instalment: '',
      start_year: new Date().getFullYear(),
      start_month: new Date().getMonth() + 1, notes: '',
    });
  };

  const submitCreate = async () => {
    if (!form.user_id || !form.reason.trim() || !form.total_amount) {
      toast.error(isAr ? 'يرجى إكمال الحقول المطلوبة' : 'Please complete the required fields');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/deductions`, {
        user_id: form.user_id,
        reason: form.reason.trim(),
        total_amount: parseFloat(form.total_amount),
        monthly_instalment: form.monthly_instalment
          ? parseFloat(form.monthly_instalment)
          : null,
        start_year: parseInt(form.start_year, 10),
        start_month: parseInt(form.start_month, 10),
        notes: form.notes.trim(),
      }, { withCredentials: true });
      toast.success(isAr ? 'تم إنشاء الخصم ✓' : 'Deduction created ✓');
      setOpenCreate(false);
      resetForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل الإنشاء' : 'Create failed'));
    } finally {
      setSaving(false);
    }
  };

  const submitCancel = async () => {
    if (!cancelTarget || !cancelReason.trim()) {
      toast.error(isAr ? 'يرجى إدخال سبب الإلغاء' : 'Please enter the reason');
      return;
    }
    setCancelling(true);
    try {
      await axios.post(`${API}/deductions/${cancelTarget.id}/cancel`, {
        reason: cancelReason.trim(),
      }, { withCredentials: true });
      toast.success(isAr ? 'تم إلغاء الخصم' : 'Deduction cancelled');
      setCancelTarget(null);
      setCancelReason('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل الإلغاء' : 'Cancel failed'));
    } finally {
      setCancelling(false);
    }
  };

  // ─── Edit an existing deduction ──────────────────────────────────────
  // The backend only allows edits while status='active' and the deduction
  // has never been applied to a payroll cycle (applied_history empty).
  // We pre-fill the form with the existing values then send whatever the
  // admin changes as a PATCH.
  const openEdit = (row) => {
    setEditTarget(row);
    setForm({
      user_id: row.user_id,
      reason: row.reason || '',
      total_amount: String(row.total_amount ?? ''),
      monthly_instalment: String(row.monthly_instalment ?? ''),
      start_year: row.start_year,
      start_month: row.start_month,
      notes: row.notes || '',
    });
  };

  const submitEdit = async () => {
    if (!editTarget) return;
    if (!form.reason.trim() || !form.total_amount) {
      toast.error(isAr ? 'يرجى إكمال الحقول المطلوبة' : 'Please complete the required fields');
      return;
    }
    setEditing(true);
    try {
      await axios.patch(`${API}/deductions/${editTarget.id}`, {
        reason: form.reason.trim(),
        total_amount: parseFloat(form.total_amount),
        monthly_instalment: form.monthly_instalment
          ? parseFloat(form.monthly_instalment)
          : null,
        start_year: parseInt(form.start_year, 10),
        start_month: parseInt(form.start_month, 10),
        notes: form.notes.trim(),
      }, { withCredentials: true });
      toast.success(isAr ? 'تم تحديث الخصم ✓' : 'Deduction updated ✓');
      setEditTarget(null);
      resetForm();
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || (isAr ? 'فشل التحديث' : 'Update failed'));
    } finally {
      setEditing(false);
    }
  };

  // A deduction is editable while it's still active AND hasn't been
  // pulled into any payroll cycle yet. `applied_history` is either
  // undefined/null (never applied) or an array. Empty array counts as
  // "not applied yet".
  const isEditable = (row) => (
    row.status === 'active'
    && (!row.applied_history || row.applied_history.length === 0)
  );

  // Sorted list of active employees for the dropdown.
  const employeeOptions = (employees || [])
    .filter((e) => e && e.role === 'employee' && !e.terminated_at)
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const [empPickerOpen, setEmpPickerOpen] = useState(false);
  const [empSearch, setEmpSearch] = useState('');
  const selectedEmployee = employeeOptions.find((e) => e.id === form.user_id) || null;

  // Manually filter — cmdk's built-in normalization strips some Arabic
  // characters (e.g. tatweel, hamza forms) which breaks name matching.
  // Doing the filter ourselves gives predictable behaviour for both
  // numeric employee-number lookup and Arabic/English name search.
  const filteredEmployees = useMemo(() => {
    const q = (empSearch || '').trim().toLowerCase();
    if (!q) return employeeOptions;
    return employeeOptions.filter((e) => {
      const haystack = [
        e.employee_number || '',
        e.name || '',
        e.name_en || '',
      ].join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [empSearch, employeeOptions]);

  return (
    <Card className="border border-[#C4BFF0]">
      <CardContent className="pt-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-[#655dc6]">
              {isAr ? 'الخصومات اليدوية' : 'Manual Deductions'}
            </h3>
            <p className="text-xs text-[#655dc6]/70 mt-0.5">
              {isAr
                ? 'سلف، مخالفات إدارية، تعويضات — تُطبَّق تلقائياً في دورة الراتب.'
                : 'Advances, fines, damage compensation — auto-applied on payroll runs.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px] bg-[#E8E6F5] border-[#C4BFF0]" data-testid="deductions-filter-status">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{isAr ? 'الكل' : 'All'}</SelectItem>
                <SelectItem value="active">{isAr ? 'نشط' : 'Active'}</SelectItem>
                <SelectItem value="completed">{isAr ? 'مكتمل' : 'Completed'}</SelectItem>
                <SelectItem value="cancelled">{isAr ? 'ملغى' : 'Cancelled'}</SelectItem>
              </SelectContent>
            </Select>
            <Button
              onClick={() => setOpenCreate(true)}
              className="bg-[#655dc6] hover:bg-[#544cb0] text-white gap-2"
              data-testid="add-deduction-btn"
            >
              <Plus size={16} weight="bold" />
              {isAr ? 'خصم جديد' : 'New Deduction'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-24">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#655dc6]" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-center py-8 text-[#655dc6]/70" data-testid="deductions-empty">
            {isAr ? 'لا توجد خصومات مسجّلة.' : 'No deductions on file.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-[#C4BFF0]">
                  <TableHead className="text-start">{isAr ? 'الرقم' : 'Emp. No.'}</TableHead>
                  <TableHead className="text-start">{isAr ? 'الموظف' : 'Employee'}</TableHead>
                  <TableHead className="text-start">{isAr ? 'السبب' : 'Reason'}</TableHead>
                  <TableHead className="text-start">{isAr ? 'الإجمالي' : 'Total'}</TableHead>
                  <TableHead className="text-start">{isAr ? 'القسط الشهري' : 'Monthly'}</TableHead>
                  <TableHead className="text-start">{isAr ? 'المتبقي' : 'Remaining'}</TableHead>
                  <TableHead className="text-start">{isAr ? 'يبدأ من' : 'Starts'}</TableHead>
                  <TableHead className="text-start">{isAr ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead className="text-start">{isAr ? 'إجراء' : 'Action'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`deduction-row-${r.id}`}>
                    <TableCell className="font-mono text-xs text-[#655dc6]/80">{empNo(r.user_id) || '-'}</TableCell>
                    <TableCell>
                      <EmployeeName
                        name={r.employee_name}
                        nameEn={r.employee_name_en}
                        badgeData={getBadge(r.user_id)}
                        badgeLanguage={language}
                      />
                    </TableCell>
                    <TableCell className="text-[#655dc6]/80 max-w-[220px] truncate" title={r.reason}>{r.reason}</TableCell>
                    <TableCell className="font-medium">{fmtSar(r.total_amount)} SAR</TableCell>
                    <TableCell>{fmtSar(r.monthly_instalment)} SAR</TableCell>
                    <TableCell className={r.remaining_amount > 0 ? 'font-semibold text-amber-700' : 'text-[#655dc6]/60'}>
                      {fmtSar(r.remaining_amount)} SAR
                    </TableCell>
                    <TableCell className="font-mono text-xs">{String(r.start_month).padStart(2, '0')}/{r.start_year}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_BADGE[r.status]?.cls}>
                        {STATUS_BADGE[r.status]?.[isAr ? 'ar' : 'en'] || r.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {r.status === 'active' && (
                        <div className="flex gap-1">
                          {isEditable(r) && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEdit(r)}
                              className="border-[#655dc6]/40 text-[#655dc6] hover:bg-[#655dc6]/10 h-8 gap-1"
                              data-testid={`edit-deduction-${r.id}`}
                              title={isAr ? 'تعديل قبل توليد الرواتب' : 'Edit before payroll generation'}
                            >
                              <PencilSimple size={14} weight="bold" />
                              {isAr ? 'تعديل' : 'Edit'}
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setCancelTarget(r); setCancelReason(''); }}
                            className="border-red-300 text-red-600 hover:bg-red-50 h-8 gap-1"
                            data-testid={`cancel-deduction-${r.id}`}
                          >
                            <XCircle size={14} weight="bold" />
                            {isAr ? 'إلغاء' : 'Cancel'}
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={(v) => { if (!v) { setOpenCreate(false); resetForm(); } }}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="sm:max-w-[540px]" data-testid="deduction-create-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <Plus size={22} weight="duotone" className="text-[#655dc6]" />
              {isAr ? 'إضافة خصم جديد' : 'Add New Deduction'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#655dc6]/80">{isAr ? 'الموظف' : 'Employee'} *</Label>
              <Popover open={empPickerOpen} onOpenChange={(v) => { setEmpPickerOpen(v); if (!v) setEmpSearch(''); }}>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    role="combobox"
                    aria-expanded={empPickerOpen}
                    className="w-full flex items-center justify-between bg-[#E8E6F5] border border-[#C4BFF0] rounded-md px-3 py-2 text-sm text-start hover:bg-[#DED8F1] transition-colors"
                    data-testid="deduction-user-picker"
                  >
                    {selectedEmployee
                      ? (
                        <span className="flex items-center gap-2 truncate">
                          {selectedEmployee.employee_number && (
                            <span className="font-mono text-xs text-[#655dc6]/70 tabular-nums">
                              {selectedEmployee.employee_number}
                            </span>
                          )}
                          <span className="truncate">{selectedEmployee.name}</span>
                        </span>
                      )
                      : <span className="text-[#655dc6]/60">{isAr ? 'ابحث بالاسم أو الرقم الوظيفي…' : 'Search by name or employee number…'}</span>}
                    <CaretDown size={14} weight="bold" className="text-[#655dc6]/60 shrink-0" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="p-0 w-[--radix-popover-trigger-width] max-h-[340px]" align="start">
                  <div className="p-2 border-b border-[#C4BFF0]">
                    <Input
                      value={empSearch}
                      onChange={(e) => setEmpSearch(e.target.value)}
                      placeholder={isAr ? 'الرقم الوظيفي أو الاسم…' : 'Employee number or name…'}
                      className="h-8 bg-white border-[#C4BFF0]"
                      autoFocus
                      data-testid="deduction-user-search"
                    />
                  </div>
                  <div className="max-h-[260px] overflow-y-auto py-1">
                    {filteredEmployees.length === 0 ? (
                      <div className="px-3 py-6 text-center text-sm text-[#655dc6]/60">
                        {isAr ? 'لا يوجد موظف مطابق.' : 'No employee found.'}
                      </div>
                    ) : filteredEmployees.map((e) => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => {
                          setForm((prev) => ({ ...prev, user_id: e.id }));
                          setEmpPickerOpen(false);
                          setEmpSearch('');
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-start hover:bg-[#F2F0FA] transition-colors"
                        data-testid={`deduction-user-option-${e.id}`}
                      >
                        <span className="font-mono text-xs text-[#655dc6]/70 tabular-nums w-14 shrink-0">
                          {e.employee_number || '—'}
                        </span>
                        <span className="flex-1 truncate text-[#2D2A5C]">{e.name}</span>
                        {e.department && (
                          <span className="text-[10px] text-[#655dc6]/50 truncate max-w-[110px]">
                            {e.department}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div>
              <Label className="text-xs text-[#655dc6]/80">{isAr ? 'السبب' : 'Reason'} *</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                placeholder={isAr ? 'مثال: سلفة راتب، تعويض تلف…' : 'e.g. Salary advance, damage recovery…'}
                className="bg-[#E8E6F5] border-[#C4BFF0]"
                maxLength={200}
                data-testid="deduction-reason"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#655dc6]/80">{isAr ? 'المبلغ الإجمالي (SAR)' : 'Total Amount (SAR)'} *</Label>
                <Input
                  type="number" min="1" step="1"
                  value={form.total_amount}
                  onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                  className="bg-[#E8E6F5] border-[#C4BFF0]"
                  data-testid="deduction-total"
                />
              </div>
              <div>
                <Label className="text-xs text-[#655dc6]/80">{isAr ? 'قسط شهري (اختياري)' : 'Monthly Instalment (optional)'}</Label>
                <Input
                  type="number" min="1" step="1"
                  value={form.monthly_instalment}
                  onChange={(e) => setForm({ ...form, monthly_instalment: e.target.value })}
                  placeholder={isAr ? 'اتركه فارغاً للخصم مرة واحدة' : 'Leave blank for one-time'}
                  className="bg-[#E8E6F5] border-[#C4BFF0]"
                  data-testid="deduction-instalment"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#655dc6]/80">{isAr ? 'يبدأ من شهر' : 'Start Month'}</Label>
                <Select value={String(form.start_month)} onValueChange={(v) => setForm({ ...form, start_month: parseInt(v, 10) })}>
                  <SelectTrigger className="bg-[#E8E6F5] border-[#C4BFF0]" data-testid="deduction-start-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                      <SelectItem key={m} value={String(m)}>{String(m).padStart(2, '0')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#655dc6]/80">{isAr ? 'يبدأ من سنة' : 'Start Year'}</Label>
                <Select value={String(form.start_year)} onValueChange={(v) => setForm({ ...form, start_year: parseInt(v, 10) })}>
                  <SelectTrigger className="bg-[#E8E6F5] border-[#C4BFF0]" data-testid="deduction-start-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs text-[#655dc6]/80">{isAr ? 'ملاحظات (اختياري)' : 'Notes (optional)'}</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                maxLength={400}
                className="bg-[#E8E6F5] border-[#C4BFF0]"
                data-testid="deduction-notes"
              />
            </div>

            <div className="text-[10px] text-[#655dc6]/70 flex items-start gap-1.5 bg-[#F2F0FA] border border-[#C4BFF0] rounded p-2">
              <Warning size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
              <span>
                {isAr
                  ? 'الخصم سيُطبَّق تلقائياً في كل دورة راتب تبدأ من الشهر المحدد. القسط ≤ الإجمالي؛ إذا لم يُحدَّد قسط، سيُخصم المبلغ كاملاً مرة واحدة.'
                  : 'The deduction is applied automatically each payroll cycle starting from the chosen month. Instalment ≤ total; if left blank the full amount is deducted once.'}
              </span>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setOpenCreate(false); resetForm(); }} className="text-[#655dc6]" data-testid="deduction-cancel-btn">
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={submitCreate} disabled={saving} className="bg-[#655dc6] hover:bg-[#544cb0] text-white" data-testid="deduction-save-btn">
              {saving ? (isAr ? 'جارٍ الحفظ…' : 'Saving…') : (isAr ? 'حفظ' : 'Save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog — same fields as Create, prefilled */}
      <Dialog open={!!editTarget} onOpenChange={(v) => { if (!v) { setEditTarget(null); resetForm(); } }}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="sm:max-w-[540px]" data-testid="deduction-edit-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#655dc6] flex items-center gap-2">
              <PencilSimple size={22} weight="duotone" className="text-[#655dc6]" />
              {isAr ? 'تعديل الخصم' : 'Edit Deduction'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-[#F2F0FA] border border-[#C4BFF0] rounded p-2 text-xs text-[#655dc6]">
              <strong>{isAr ? 'الموظف: ' : 'Employee: '}</strong>{editTarget?.employee_name}
            </div>
            <div>
              <Label className="text-xs text-[#655dc6]/80">{isAr ? 'السبب' : 'Reason'} *</Label>
              <Input
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                className="bg-[#E8E6F5] border-[#C4BFF0]"
                data-testid="deduction-edit-reason"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#655dc6]/80">{isAr ? 'المبلغ الإجمالي' : 'Total Amount'} *</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.total_amount}
                  onChange={(e) => setForm({ ...form, total_amount: e.target.value })}
                  className="bg-[#E8E6F5] border-[#C4BFF0]"
                  data-testid="deduction-edit-total"
                />
              </div>
              <div>
                <Label className="text-xs text-[#655dc6]/80">{isAr ? 'القسط الشهري' : 'Monthly Instalment'}</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={form.monthly_instalment}
                  onChange={(e) => setForm({ ...form, monthly_instalment: e.target.value })}
                  placeholder={isAr ? 'اتركه فارغاً لدفعة واحدة' : 'Leave blank for one-shot'}
                  className="bg-[#E8E6F5] border-[#C4BFF0]"
                  data-testid="deduction-edit-instalment"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#655dc6]/80">{isAr ? 'شهر بداية السريان' : 'Start Month'} *</Label>
                <Select value={String(form.start_month)} onValueChange={(v) => setForm({ ...form, start_month: parseInt(v, 10) })}>
                  <SelectTrigger className="bg-[#E8E6F5] border-[#C4BFF0]" data-testid="deduction-edit-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                      <SelectItem key={m} value={String(m)}>{String(m).padStart(2,'0')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-[#655dc6]/80">{isAr ? 'سنة بداية السريان' : 'Start Year'} *</Label>
                <Input
                  type="number" min="2020" max="2100"
                  value={form.start_year}
                  onChange={(e) => setForm({ ...form, start_year: e.target.value })}
                  className="bg-[#E8E6F5] border-[#C4BFF0]"
                  data-testid="deduction-edit-year"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#655dc6]/80">{isAr ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
                maxLength={400}
                className="bg-[#E8E6F5] border-[#C4BFF0]"
                data-testid="deduction-edit-notes"
              />
            </div>
            <div className="text-[10px] text-[#655dc6]/70 flex items-start gap-1.5 bg-amber-50 border border-amber-200 rounded p-2">
              <Warning size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
              <span>
                {isAr
                  ? 'التعديل ممكن فقط قبل أن يشمل أي كشف رواتب هذا الخصم. بمجرد إدراجه في كشف مسودة، لا يمكن تعديله — يجب حذف المسودة أولاً.'
                  : 'Editing is only possible before any payroll cycle includes this deduction. Once picked up by a draft, delete the draft first.'}
              </span>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => { setEditTarget(null); resetForm(); }} className="text-[#655dc6]" data-testid="deduction-edit-cancel-btn">
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button onClick={submitEdit} disabled={editing} className="bg-[#655dc6] hover:bg-[#544cb0] text-white" data-testid="deduction-edit-save-btn">
              {editing ? (isAr ? 'جارٍ الحفظ…' : 'Saving…') : (isAr ? 'حفظ التعديل' : 'Save changes')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel dialog */}
      <Dialog open={!!cancelTarget} onOpenChange={(v) => { if (!v) setCancelTarget(null); }}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="sm:max-w-[420px]" data-testid="deduction-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle size={22} weight="duotone" />
              {isAr ? 'إلغاء الخصم' : 'Cancel Deduction'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#655dc6]/80">
            {cancelTarget && (isAr
              ? <>سيتم إلغاء الخصم على <strong>{cancelTarget.employee_name}</strong> والمبلغ المتبقي <strong>{fmtSar(cancelTarget.remaining_amount)} ر.س</strong>. لا يمكن التراجع.</>
              : <>Cancelling deduction on <strong>{cancelTarget.employee_name}</strong> with remaining <strong>{fmtSar(cancelTarget.remaining_amount)} SAR</strong>. This cannot be undone.</>)}
          </p>
          <div>
            <Label className="text-xs text-[#655dc6]/80">{isAr ? 'سبب الإلغاء' : 'Reason'} *</Label>
            <Textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              maxLength={300}
              className="bg-[#E8E6F5] border-[#C4BFF0]"
              data-testid="deduction-cancel-reason"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setCancelTarget(null)} className="text-[#655dc6]" data-testid="deduction-cancel-close">
              {isAr ? 'رجوع' : 'Back'}
            </Button>
            <Button onClick={submitCancel} disabled={cancelling} className="bg-red-600 hover:bg-red-700 text-white" data-testid="deduction-cancel-confirm">
              {cancelling ? (isAr ? 'جارٍ الإلغاء…' : 'Cancelling…') : (isAr ? 'تأكيد الإلغاء' : 'Confirm Cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
